import { APP_SECTION_ID } from '@efficiently/client-core-routing';
import type { AppShellRailButton } from '@efficiently/client-ui-app-shell';

/** The AI Chat rail button: the panel defaults seed it, and the Clarity environment fills it with the chat. */
export const AI_CHAT_BUTTON_ID = 'ai-chat';

/** The shell's default panel width, `--eff-sys-layout-drawer-width`, in px. */
const DEFAULT_PANEL_WIDTH_PX = 336;

/** The AI Chat panel opens at 460px; its resize handle still narrows it to the default width. */
const AI_CHAT_PANEL_WIDTH_PX = 460;

/** In Control Center the AI Chat panel opens at 700px instead. */
const CONTROL_CENTER_AI_CHAT_PANEL_WIDTH_PX = 700;

/**
 * The controls of the platform shell's left icon rail, in the order the rail renders them.
 *
 * They live beside the shell rather than inside `@efficiently/client-ui-app-shell` for exactly the
 * reason `APP_SECTIONS` lives outside it: that library is `type:ui` and knows no button by name
 * (TR-1). The table crosses the seam as DATA, handed over in the shell's constructor, and nothing
 * in the library imports this file.
 *
 * **AI Chat is the only one.** `clarity-environment.routes.ts` registers the domain chat panel against
 * it from an injector above every section, so the button opens the chat in Control Center, Design and
 * Supply alike. The domain list is not a rail button: it is the domain navigator, toggled by the
 * chevron the shell draws over the rail's centre.
 *
 * The icon comes from the generated sprite — `ai-chat` is the custom design-system glyph under
 * `ui-icons/assets/icons/custom/`. `as const satisfies` is what checks that rather than trusting it:
 * the assertion narrows `icon` to its literal, and the `satisfies` fails to compile on a name absent
 * from `EffIconName` instead of rendering an empty glyph at runtime.
 */
export const APP_RAIL_BUTTONS = [
	{
		id: AI_CHAT_BUTTON_ID,
		label: 'AI Chat',
		panelTitle: 'Clarity AI',
		icon: 'ai-chat',
		panelScale: AI_CHAT_PANEL_WIDTH_PX / DEFAULT_PANEL_WIDTH_PX,
		sectionPanelScales: {
			[APP_SECTION_ID.CONTROL_CENTER]: CONTROL_CENTER_AI_CHAT_PANEL_WIDTH_PX / DEFAULT_PANEL_WIDTH_PX,
		},
	},
] as const satisfies readonly AppShellRailButton[];
